<template>
    <portal to="modal">
        <transition leave-active-class="duration-100" @before-enter="beforeOpen" @after-leave="afterClose">
            <div v-show="show" class="media-modal-wrapper fixed inset-0 z-70 overflow-y-auto">
                <div id="media-modal-close" class="fixed top-10 right-12 z-50 media-fit">
                    <div class="has-tooltip">
                        <button
                            class="p-1 focus:outline-none text-gray-100 bg-gray-500 hover:text-indigo-500 focus:text-indigo-500 hover:bg-gray-100 focus:bg-gray-100 hover:shadow-sm focus:shadow-sm rounded-full focus:ring-1 focus:ring-offset-1 focus:ring-offset-indigo-100 focus:ring-indigo-500"
                            type="button"
                            @click="close"
                        >
                            <svg class="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path d="M6 18L18 6M6 6l12 12" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" />
                            </svg>
                            <span class="sr-only">Close</span>
                        </button>
                        <tooltip position="bottom">Close</tooltip>
                    </div>
                </div>

                <transition enter-active-class="ease-out duration-300" enter-class="opacity-0" enter-to-class="opacity-100" leave-active-class="ease-in duration-100" leave-class="opacity-100" leave-to-class="opacity-0">
                    <div v-show="show" class="fixed inset-0 transform transition-all bg-gray-900 bg-opacity-50" @click="close"></div>
                </transition>
                <transition
                    enter-active-class="ease-out duration-300"
                    enter-class="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    enter-to-class="opacity-100 translate-y-0 sm:scale-100"
                    leave-active-class="ease-in duration-100"
                    leave-class="opacity-100 translate-y-0 sm:scale-100"
                    leave-to-class="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                >
                    <div v-show="show && !playlistUrl" :class="{ 'max-w-7xl mx-auto': mediaFit }" class="relative">
                        <div id="media-modal" v-touch:swipe.left="toNextImage" v-touch:swipe.right="toPrevImage" v-touch:swipe.up="close" :class="{ 'media-fit': mediaFit }" @click="toggleMediaFit" v-html="currentImage"></div>

                        <!-- Arrow Key Events -->
                        <Keypress :key-code="38" key-event="keyup" @success="close" />
                        <Keypress :key-code="37" key-event="keyup" @success="toPrevImage" />
                        <Keypress :key-code="39" key-event="keyup" @success="toNextImage" />
                    </div>
                </transition>

                <transition
                    enter-active-class="ease-out duration-300"
                    enter-class="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    enter-to-class="opacity-100 translate-y-0 sm:scale-100"
                    leave-active-class="ease-in duration-100"
                    leave-class="opacity-100 translate-y-0 sm:scale-100"
                    leave-to-class="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                >
                    <div v-if="isVideo" v-show="show && playlistUrl" :class="{ 'max-w-7xl mx-auto': mediaFit }" class="relative">
                        <div id="media-modal-player" :class="{ 'media-fit': mediaFit }">
                            <video-player :options="videoPlayerOptions"></video-player>
                        </div>

                        <!-- Arrow Key Events -->
                        <Keypress :key-code="38" key-event="keyup" @success="close" />
                    </div>
                </transition>
            </div>
        </transition>
    </portal>
</template>

<script>
import Modal from "@/Jetstream/Modal"
import Keypress from "vue-keypress"
import Tooltip from "@/Components/Tooltip"
import VideoPlayer from "@/Components/VideoPlayer"

export default {
    components: {
        Tooltip,
        Modal,
        Keypress,
        VideoPlayer,
    },

    created() {
        const closeOnEscape = (e) => {
            if (e.key === "Escape" && this.show) {
                this.close()
            }
        }

        document.addEventListener("keydown", closeOnEscape)

        this.$once("hook:destroyed", () => {
            document.removeEventListener("keydown", closeOnEscape)
        })
    },

    props: {
        show: {
            default: false,
        },
        maxWidth: {
            default: "5xl",
        },
        maxHeight: {
            default: null,
        },
        closeable: {
            default: true,
        },
        images: {
            type: Array,
            default: () => [],
        },
        image: {
            type: String,
            default: null,
        },
        playlistUrl: {
            type: String,
            default: null,
        },
        videoPlayerOptions: {
            type: Object,
            default: () => {},
        },
    },

    computed: {
        isVideo() {
            return _.includes(this.image, "</video>")
        },
        currentIndex() {
            return this.images.findIndex((image) => image === this.image)
        },
        currentImage() {
            return this.image
        },
        prevImage() {
            return this.images[this.currentIndex === 0 ? this.images.length - 1 : this.currentIndex - 1]
        },
        nextImage() {
            return this.images[this.currentIndex === this.images.length - 1 ? 0 : this.currentIndex + 1]
        },
        hasTitle() {
            return !!this.$slots.title
        },
        hasFooter() {
            return !!this.$slots.footer
        },
    },

    data() {
        return {
            mediaFit: true,
        }
    },

    methods: {
        toggleMediaFit() {
            if (!this.isVideo) {
                this.mediaFit = !this.mediaFit
            }
        },

        beforeOpen() {
            document.body.style.overflow = "hidden"
            document.body.style.paddingRight = "15px"
        },

        afterClose() {
            document.body.style.overflow = null
            document.body.style.paddingRight = null
            this.mediaFit = true
        },

        toPrevImage() {
            if (!this.show) return null
            this.$emit("update:image", this.prevImage)
        },

        toNextImage() {
            if (!this.show) return null
            this.$emit("update:image", this.nextImage)
        },

        close() {
            if (!this.show) return null
            this.$emit("close")
        },
    },
}
</script>
